// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: voice for agents (design/agents-side-bar r2-v1..v3). While Konvo answers about coding
// agents, the server says what the answer is about (KONVO_ASSISTANT_VOICE `focus.agents`, sent by
// the Agents tools): the threads it names, the approval or question it answers, what "Open it?"
// opens, and sometimes what to open now. VoiceAgentsPointer outlines those rows and cards on the
// Agents page; when none is on screen the voice card offers to open them.
//
// The focus belongs to one answer: it goes when the person starts the next turn, when voice mode
// ends, and after a while on its own.

import {makeAutoObservable} from 'mobx';

export type AgentsOpenPanel = 'thread' | 'changes' | 'pull';

export interface VoiceAgentsThread {
	machineId: string;
	id: string;
	title: string;
}

export interface VoiceAgentsRequest {
	machineId: string;
	threadId: string;
	id: string;
	kind: 'approval' | 'question';
}

export interface VoiceAgentsTarget {
	machineId: string;
	/** Null: the Agents list. */
	threadId: string | null;
	panel: AgentsOpenPanel | null;
	from?: number;
	to?: number;
}

export interface VoiceAgentsFocusData {
	threads: Array<VoiceAgentsThread>;
	request: VoiceAgentsRequest | null;
	offer: VoiceAgentsTarget | null;
	open: VoiceAgentsTarget | null;
}

/** A focus answer goes stale on its own after this long. */
const FOCUS_FOR_MS = 120_000;
const MAX_THREADS = 12;

const text = (value: unknown, max: number): string | null =>
	typeof value === 'string' && value.trim() ? value.trim().slice(0, max) : null;

function target(raw: unknown): VoiceAgentsTarget | null {
	if (!raw || typeof raw !== 'object') return null;
	const value = raw as Record<string, unknown>;
	const machineId = text(value.machine_id, 100);
	if (!machineId) return null;
	const panel = value.panel === 'thread' || value.panel === 'changes' || value.panel === 'pull' ? value.panel : null;
	const turn = (n: unknown) => (typeof n === 'number' && Number.isInteger(n) && n >= 0 ? n : undefined);
	const from = turn(value.from);
	const to = turn(value.to);
	return {
		machineId,
		threadId: text(value.thread_id, 200),
		panel,
		...(panel === 'changes' && from !== undefined && to !== undefined && to > from ? {from, to} : {}),
	};
}

/** The server's `focus.agents`, checked; null when there is nothing in it. */
export function parseAgentsFocus(raw: unknown): VoiceAgentsFocusData | null {
	if (!raw || typeof raw !== 'object') return null;
	const value = raw as Record<string, unknown>;
	const threads: Array<VoiceAgentsThread> = [];
	for (const entry of Array.isArray(value.threads) ? value.threads.slice(0, MAX_THREADS) : []) {
		if (!entry || typeof entry !== 'object') continue;
		const thread = entry as Record<string, unknown>;
		const machineId = text(thread.machine_id, 100);
		const id = text(thread.id, 200);
		if (machineId && id) threads.push({machineId, id, title: text(thread.title, 120) ?? ''});
	}
	let request: VoiceAgentsRequest | null = null;
	if (value.request && typeof value.request === 'object') {
		const raw = value.request as Record<string, unknown>;
		const machineId = text(raw.machine_id, 100);
		const threadId = text(raw.thread_id, 200);
		const id = text(raw.id, 300);
		const kind = raw.kind === 'approval' || raw.kind === 'question' ? raw.kind : null;
		if (machineId && threadId && id && kind) request = {machineId, threadId, id, kind};
	}
	const offer = target(value.offer);
	const open = target(value.open);
	if (threads.length === 0 && !request && !offer && !open) return null;
	return {threads, request, offer, open};
}

/** The key Agents rows and panels use for a thread. */
export const agentThreadKey = (machineId: string, threadId: string) => `${machineId}/${threadId}`;

const THREAD_SELECTOR = '[data-flx="konvo-agents.thread"]';
const SHOWN_WITHIN_MS = 5000;
/** After the thread view's own first effects (it closes the panel of the thread before). */
const SETTLE_MS = 120;

/** Resolves once a thread view is on the page (or after a while regardless). */
function threadShown(): Promise<void> {
	return new Promise((resolve) => {
		const since = Date.now();
		const check = () => {
			if (typeof document === 'undefined' || document.querySelector(THREAD_SELECTOR)) {
				setTimeout(resolve, SETTLE_MS);
				return;
			}
			if (Date.now() - since > SHOWN_WITHIN_MS) resolve();
			else setTimeout(check, 50);
		};
		setTimeout(check, 0);
	});
}

/**
 * Put a thread, its changes or its pull request (or the Agents list) on screen. The Agents page's
 * state loads on first use, so voice mode does not pull it in on every page.
 */
export async function openAgentsTarget(goal: VoiceAgentsTarget): Promise<void> {
	const [
		{Routes},
		{default: Agents},
		{parseAgentsPath},
		{default: AgentSidePanel},
		{default: Navigation},
		RouterUtils,
	] = await Promise.all([
		import('@app/app/Routes'),
		import('@app/features/konvo_agents/state/Agents'),
		import('@app/features/konvo_agents/state/AgentsModel'),
		import('@app/features/konvo_agents/state/AgentSidePanel'),
		import('@app/features/navigation/state/Navigation'),
		import('@app/features/navigation/utils/RouterUtils'),
	]);
	if (!goal.threadId) {
		Agents.pickListMachine(goal.machineId);
		RouterUtils.transitionTo(Routes.agents());
		return;
	}
	const here = parseAgentsPath(Navigation.pathname);
	if (here.machineId !== goal.machineId || here.threadId !== goal.threadId) {
		RouterUtils.transitionTo(Routes.agents(goal.machineId, goal.threadId));
		// A thread that opens closes whatever panel was open: the panel goes up once it is on screen.
		if (goal.panel === 'pull' || goal.panel === 'changes') await threadShown();
	}
	const key = agentThreadKey(goal.machineId, goal.threadId);
	if (goal.panel === 'pull') AgentSidePanel.open(key, {kind: 'pull'});
	else if (goal.panel === 'changes' && goal.from !== undefined && goal.to !== undefined)
		AgentSidePanel.open(key, {
			kind: 'changes',
			target: {from: goal.from, to: goal.to, scope: goal.from === 0 ? 'thread' : 'turn'},
		});
}

export class VoiceAgentsFocusStore {
	current: VoiceAgentsFocusData | null = null;
	private timer: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		makeAutoObservable<this, 'timer'>(this, {timer: false}, {autoBind: true});
	}

	/** The server's focus for the answer being given; opens what it asks to open. */
	receive(raw: unknown): void {
		const focus = parseAgentsFocus(raw);
		if (!focus) return;
		this.current = focus;
		if (this.timer) clearTimeout(this.timer);
		this.timer = setTimeout(this.clear, FOCUS_FOR_MS);
		if (focus.open) void openAgentsTarget(focus.open);
	}

	/** What "Open it?" opens: the offer, else the first thread named. */
	get offerTarget(): VoiceAgentsTarget | null {
		const focus = this.current;
		if (!focus) return null;
		if (focus.offer) return focus.offer;
		const first = focus.threads[0];
		return first ? {machineId: first.machineId, threadId: first.id, panel: 'thread'} : null;
	}

	/** The title "Open it?" names. */
	get offerTitle(): string {
		const goal = this.offerTarget;
		if (!goal?.threadId) return '';
		return this.current?.threads.find((thread) => thread.id === goal.threadId)?.title ?? '';
	}

	openOffer(): void {
		const goal = this.offerTarget;
		if (goal) void openAgentsTarget(goal);
	}

	clear(): void {
		if (this.timer) clearTimeout(this.timer);
		this.timer = null;
		this.current = null;
	}
}

const VoiceAgentsFocus = new VoiceAgentsFocusStore();
export default VoiceAgentsFocus;
